import { createContext, useContext, useState, useEffect, useRef } from 'react';
import type { User } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { supabase } from '../services/supabaseClient';
import { sanitizeError, classifyAuthError } from '../utils/errorMessages';
import analytics from '../services/analyticsService';
import { getAttribution } from '../lib/attribution';
import type { Profile, ProfileRole } from '../types';
import type { AuthContextValue, LoginResult, AuthResult } from '../types';

const AuthContext = createContext<AuthContextValue | null>(null);

export { AuthContext };

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState('');
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const currentUserIdRef = useRef<string | null>(null); // Evita setUser duplicado

  // Carregar perfil do usuário
  async function loadProfile(userId: string): Promise<Profile | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      // Ignorar AbortError - causado pelo React StrictMode
      if (error.message?.includes('AbortError')) {
        console.log('[AuthContext] loadProfile AbortError ignorado');
        return null;
      }
      console.error('Erro ao carregar perfil:', error);
      return null;
    }
    return data as Profile;
  }

  // Verificar sessão ao carregar
  useEffect(() => {
    let isMounted = true;

    // Erro devolvido pelo provedor no callback de OAuth (ex.: access_denied,
    // server_error). Supabase entrega em query (?error=) ou no hash (#error=).
    // O fluxo de reset de senha trata o seu próprio caso (ResetPasswordScreen),
    // então o ignoramos aqui para não contar em dobro. error_description é texto
    // livre (pode ter PII) e NÃO é logado.
    if (window.location.pathname !== '/reset-password') {
      const search = new URLSearchParams(window.location.search);
      const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const oauthError = search.get('error') || hash.get('error');
      if (oauthError) {
        const rawCode = search.get('error_code') || hash.get('error_code') || oauthError;
        const normalized = classifyAuthError(rawCode);
        analytics.trackAuthError(normalized === 'unknown' ? rawCode : normalized, 'oauth');
      }
    }

    async function getSession() {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (isMounted && session?.user) {
          const profileData = await loadProfile(session.user.id);
          if (isMounted) {
            // Verificar se o usuário está bloqueado
            if (profileData?.is_blocked) {
              await supabase.auth.signOut();
              currentUserIdRef.current = null;
              setUser(null);
              setProfile(null);
              Sentry.setUser(null);
              setError(profileData.blocked_reason || 'Sua conta foi bloqueada pelo administrador.');
              return;
            }
            currentUserIdRef.current = session.user.id;
            setUser(session.user);
            setProfile(profileData);
            Sentry.setUser({ id: session.user.id, email: session.user.email });
          }
        }
      } catch (err: unknown) {
        // Ignorar AbortError - causado pelo React StrictMode
        const e = err as Error;
        if (e?.message?.includes('AbortError') || e?.name === 'AbortError') {
          console.log('[AuthContext] getSession AbortError ignorado');
          // NÃO fazer return - deixar o finally executar!
        } else {
          console.error('Erro ao verificar sessão:', err);
        }
      } finally {
        // SEMPRE executar, mesmo após AbortError
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    getSession();

    // Listener para mudanças de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;

        console.log('[AuthContext] onAuthStateChange:', event, 'userId:', session?.user?.id);

        if (session?.user) {
          // Só atualiza se o user realmente mudou (evita loops)
          if (currentUserIdRef.current !== session.user.id) {
            console.log('[AuthContext] User changed, updating state');
            currentUserIdRef.current = session.user.id;
            setUser(session.user);
            Sentry.setUser({ id: session.user.id, email: session.user.email });

            // OAuth (Google): distinguir cadastro novo de login recorrente. Numa
            // 1ª sessão, created_at ≈ last_sign_in_at (ambos vêm do servidor, sem
            // depender do relógio do cliente). Cadastro novo conta como sign_up.
            const provider = session.user.app_metadata?.provider;
            const isOAuth = !!provider && provider !== 'email';
            let isNewOAuthSignup = false;
            if (isOAuth) {
              const createdAt = session.user.created_at ? Date.parse(session.user.created_at) : 0;
              const lastSignIn = session.user.last_sign_in_at ? Date.parse(session.user.last_sign_in_at) : createdAt;
              isNewOAuthSignup = Math.abs(lastSignIn - createdAt) < 10000;
              if (isNewOAuthSignup) {
                void analytics.trackSignUp(provider, session.user.email);
              } else {
                analytics.trackLogin(provider);
              }
            }

            // Carregar perfil em background (não bloqueia o loading)
            loadProfile(session.user.id).then(profileData => {
              if (isMounted) {
                if (profileData?.is_blocked) {
                  supabase.auth.signOut();
                  currentUserIdRef.current = null;
                  setUser(null);
                  setProfile(null);
                  Sentry.setUser(null);
                  setError(profileData.blocked_reason || 'Sua conta foi bloqueada pelo administrador.');
                  return;
                }
                setProfile(profileData);

                // Cadastro novo via Google: o trigger não recebe gclid/UTMs no
                // fluxo OAuth, então gravamos a atribuição first-touch aqui.
                // Idempotente (só quando ainda não houver atribuição no perfil).
                if (isOAuth && isNewOAuthSignup && !profileData?.attribution_captured_at) {
                  const attribution = getAttribution();
                  if (Object.keys(attribution).length > 0) {
                    supabase
                      .from('profiles')
                      .update({ ...attribution, attribution_captured_at: new Date().toISOString() })
                      .eq('id', session.user.id)
                      .is('attribution_captured_at', null)
                      .then(({ error: updErr }) => {
                        if (updErr) console.error('[AuthContext] Falha ao gravar atribuição OAuth:', updErr.message);
                      });
                  }
                }
              }
            });
          } else {
            console.log('[AuthContext] Same user, skipping update');
          }
        } else {
          currentUserIdRef.current = null;
          setUser(null);
          setProfile(null);
          Sentry.setUser(null);
        }
        // SEMPRE liberar loading imediatamente
        setLoading(false);
      }
    );

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Detectar bloqueio em tempo real via Supabase Realtime
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase.channel(`profile-block-${user.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'profiles',
        filter: `id=eq.${user.id}`
      }, (payload: { new: Record<string, unknown> }) => {
        if (payload.new?.is_blocked === true) {
          supabase.auth.signOut();
          currentUserIdRef.current = null;
          setUser(null);
          setProfile(null);
          Sentry.setUser(null);
          setError((payload.new.blocked_reason as string) || 'Sua conta foi bloqueada pelo administrador.');
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  // Login com email e senha
  async function login(email: string, password: string, captchaToken?: string): Promise<LoginResult> {
    setError(null);
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken }
      });

      if (error) throw error;

      // Verificar se o usuário está bloqueado
      const profileData = await loadProfile(data.user.id);
      if (profileData?.is_blocked) {
        await supabase.auth.signOut();
        currentUserIdRef.current = null;
        setUser(null);
        setProfile(null);
        const reason = profileData.blocked_reason || 'Sua conta foi bloqueada pelo administrador.';
        setError(reason);
        return { success: false, error: reason, blocked: true };
      }

      return { success: true };
    } catch (err: unknown) {
      const errorMessage = sanitizeError(err);
      setError(errorMessage);
      return { success: false, error: errorMessage, errorCode: classifyAuthError(err) };
    } finally {
      setLoading(false);
    }
  }

  // Cadastro de novo usuário
  async function signUp(email: string, password: string, fullName: string, phoneNumber: string, whatsappConsent: boolean, captchaToken?: string): Promise<AuthResult> {
    setError(null);
    setLoading(true);

    try {
      // Atribuição first-touch (gclid/UTMs) capturada no primeiro acesso. Vai no
      // raw_user_meta_data e o trigger handle_new_user grava nas colunas do perfil.
      const attribution = getAttribution();

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          captchaToken,
          data: {
            full_name: fullName,
            role: 'member',
            phone_number: phoneNumber,
            whatsapp_consent: whatsappConsent,
            ...attribution
          }
        }
      });

      if (error) throw error;

      // Guardar email e marcar sucesso ANTES do signOut
      setSignUpEmail(email);
      setSignUpSuccess(true);

      // Fazer logout para que o usuário precise confirmar o email antes de logar
      // Isso evita que o onAuthStateChange faça login automático
      await supabase.auth.signOut();
      setUser(null);
      setProfile(null);

      return { success: true, data };
    } catch (err: unknown) {
      const errorMessage = sanitizeError(err);
      setError(errorMessage);
      return { success: false, error: errorMessage, errorCode: classifyAuthError(err) };
    } finally {
      setLoading(false);
    }
  }

  // Logout
  async function logout(): Promise<void> {
    setIsLoggingOut(true);  // Sinalizar que está fazendo logout
    setLoading(true);
    try {
      analytics.clearUser();
      Sentry.setUser(null);
      await supabase.auth.signOut();
      setUser(null);
      setProfile(null);
    } catch (err) {
      console.error('Erro ao fazer logout:', err);
    } finally {
      setLoading(false);
      setIsLoggingOut(false);
    }
  }

  // Recuperar senha
  async function resetPassword(email: string, captchaToken?: string): Promise<{ success: boolean }> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
      captchaToken
    });

    if (error) throw error;
    return { success: true };
  }

  // Login social (Google, Microsoft). `redirectTo` é a URL pra retornar APÓS
  // o callback do provedor; default = URL atual (preserva ?query=string),
  // pra fluxos como /oauth/consent?oauth_req_id=... que se perdiam antes.
  async function signInWithOAuth(provider: string, redirectTo?: string): Promise<AuthResult> {
    setError(null);
    setLoading(true);

    try {
      const target = redirectTo ?? (window.location.pathname + window.location.search);
      const normalized = target.startsWith('/') ? target : `/${target}`;
      const fullRedirect = `${window.location.origin}${normalized}`;

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: provider as 'google' | 'azure',
        options: {
          redirectTo: fullRedirect
        }
      });

      if (error) throw error;
      return { success: true, data };
    } catch (err: unknown) {
      const errorMessage = sanitizeError(err);
      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage, errorCode: classifyAuthError(err) };
    }
  }

  // Limpar estado de sucesso do cadastro
  function clearSignUpSuccess(): void {
    setSignUpSuccess(false);
    setSignUpEmail('');
  }

  // Verificar permissão por role
  function hasRole(requiredRole: ProfileRole): boolean {
    if (!profile) return false;

    const roleHierarchy: Record<ProfileRole, number> = {
      'admin': 4,
      'manager': 3,
      'member': 2,
      'viewer': 1
    };

    return roleHierarchy[profile.role] >= roleHierarchy[requiredRole];
  }

  // Verificar se é admin
  function isAdmin(): boolean {
    return profile?.role === 'admin';
  }

  async function refreshProfile(): Promise<void> {
    if (!user?.id) return;
    const profileData = await loadProfile(user.id);
    if (profileData) {
      setProfile(profileData);
    }
  }

  const value: AuthContextValue = {
    user,
    profile,
    loading,
    error,
    isAuthenticated: !!user,
    isLoggingOut,
    login,
    signUp,
    logout,
    resetPassword,
    signInWithOAuth,
    hasRole,
    isAdmin,
    signUpSuccess,
    signUpEmail,
    clearSignUpSuccess,
    refreshProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de AuthProvider');
  }
  return context;
}
